import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import DashboardLayout from "../components/layout/DashboardLayout";
import api from "../lib/api";
import { Network, CheckCircle2, Sparkles, Lock } from "lucide-react";

/**
 * Introducer Program — gated to fully approved members only.
 *
 * Access rule: user.compliance_milestone === "accredited_kyc_approved"
 * (KYC approved + Accredited approved, i.e. the same milestone that fires
 *  the celebration email fan-out).
 *
 * Members who haven't reached that milestone see a locked-state explainer.
 */
const IntroducerProgramPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/onboarding/status")
      .then(r => setStatus(r.data))
      .catch(() => setStatus({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl" data-testid="introducer-program-loading">
          <div className="h-10 w-72 bg-slate-100 rounded animate-pulse mb-4" />
          <div className="h-48 bg-slate-100 rounded-lg animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  const unlocked = status?.compliance_milestone === "accredited_kyc_approved";

  // Locked state — explain the gate
  if (!unlocked) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl" data-testid="introducer-program-locked">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={14} className="text-slate-500" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">
              Members-Only Program
            </p>
          </div>
          <h1 className="text-3xl font-serif text-slate-900 mb-4">Introducer Program</h1>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-6">
              <p className="text-sm text-slate-700 leading-relaxed">
                The EchoSense <strong>Introducer Program</strong> unlocks for members who have
                completed KYC, Accredited / Qualified Investor verification, and reached the
                <strong> Accredited / KYC Approved</strong> milestone.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed mt-3">
                Complete those verification steps in the <a className="text-blue-700 underline" href="/kyc-verification">KYC &amp; Accredited</a> portal and the program will appear here automatically.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <StatPill label="KYC" status={status?.kyc_status} />
                <StatPill label="Accredited" status={status?.accredited_status} />
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl space-y-6" data-testid="introducer-program-page">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-emerald-600" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-700 font-semibold">
              Members-Only · Network Builder
            </p>
          </div>
          <h1 className="text-3xl font-serif text-slate-900" data-testid="introducer-program-title">
            Build Your Introducer Network
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
            Refer qualified individuals into the EchoSense verification portal. Introducers may
            receive permitted referral compensation only as allowed under the final written
            structure approved by the regulated partner.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-white border-slate-200" data-testid="tier-1-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Network size={16} className="text-blue-600" />
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px]">Tier 1</Badge>
              </div>
              <p className="text-3xl font-serif font-semibold text-slate-900">Direct Introductions</p>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Members you personally introduce with your VC#. Tracked automatically on your
                <strong> My Introductees</strong> dashboard with live stage progression.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200" data-testid="tier-2-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Network size={16} className="text-blue-600" />
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px]">Tier 2</Badge>
              </div>
              <p className="text-3xl font-serif font-semibold text-slate-900">Extended Network</p>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Members introduced by your Tier 1 — the second-degree network that grows when your
                introducees become introducers themselves.
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Program Mechanics</h2>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-3"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /><span>Compliant referral tracking &amp; written disclosures on every introduction.</span></li>
              <li className="flex gap-3"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /><span>Personal VC# &amp; one-click invite link for fast, accurate attribution.</span></li>
              <li className="flex gap-3"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /><span>Live status visibility for every introduced applicant (KYC, Accredited, Partner-Transferred).</span></li>
              <li className="flex gap-3"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /><span>Compliance-safe Nudge tool to gently remind stalled introductees of their next step.</span></li>
              <li className="flex gap-3"><CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" /><span>Permitted referral compensation paid only under the final written structure approved by the regulated partner.</span></li>
            </ul>
          </CardContent>
        </Card>

        <Separator className="bg-slate-200" />
        <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="introducer-program-compliance-notice">
          EchoSense Capital Club is an introducer and verification portal. Introducers may not
          discuss returns, performance, trading strategy, fund terms, compensation amounts, or
          funding instructions with prospective or current members. All onboarding materials,
          funding instructions, and reporting come directly from the regulated partner.
        </p>
      </div>
    </DashboardLayout>
  );
};

const StatPill = ({ label, status }) => {
  const ok       = status === "approved" || status === "verified";
  const inReview = status === "in_review";
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : inReview
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <div className={`rounded-md border p-3 ${cls}`} data-testid={`stat-pill-${label.toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</p>
      <p className="text-sm font-semibold mt-0.5">{status || "not started"}</p>
    </div>
  );
};

export default IntroducerProgramPage;
